import { useEffect, useState } from "react";

/**
 * Wraps a single `MediaStreamTrack` in a `MediaRecorder` purely so it can be
 * fed to `LiveAudioVisualizer` (react-audio-visualize), which only accepts a
 * `MediaRecorder` prop AND only animates while its `.state === "recording"`
 * (it reads `.stream` for the analyser tap, but gates the draw loop on
 * `.state`) — so this recorder must actually be started, even though nothing
 * ever consumes its output (no `dataavailable` handler, no chunks kept). The
 * real recording pipeline (useRecordingSession) owns actual capture; this is
 * a second, throwaway recorder purely to satisfy the visualizer's animation
 * gate. Used for the telehealth "You" / "Everyone on the call" source cards'
 * individual waveforms (MDRS-268), matching the same visualizer the
 * single-source (in-person) waveform already uses.
 */
export const useTrackRecorder = (
  track: MediaStreamTrack | null,
  paused = false
): MediaRecorder | null => {
  const [recorder, setRecorder] = useState<MediaRecorder | null>(null);

  useEffect(() => {
    if (!track || track.readyState !== "live") {
      queueMicrotask(() => setRecorder(null));
      return;
    }
    const instance = new MediaRecorder(new MediaStream([track]));
    // No timeslice — this recorder is never read, so there's nothing to
    // gain from periodic dataavailable events, only overhead.
    instance.start();
    // Deferred (not a synchronous setState in the effect body) so creating
    // this throwaway recorder can't cascade into the same commit — matches
    // useMicPreview's own async-effect pattern for the same class of
    // "instantiate an external resource, then store it" effect.
    queueMicrotask(() => setRecorder(instance));
    return () => {
      if (instance.state !== "inactive") instance.stop();
      queueMicrotask(() => setRecorder(null));
    };
  }, [track]);

  // Mirror the real session's pause state onto this throwaway recorder too —
  // LiveAudioVisualizer freezes its last frame while `state === "paused"`,
  // matching the main (in-person) waveform's paused look instead of
  // animating a source card that has nothing new to show.
  useEffect(() => {
    if (!recorder) return;
    if (paused && recorder.state === "recording") recorder.pause();
    else if (!paused && recorder.state === "paused") recorder.resume();
  }, [recorder, paused]);

  return recorder;
};
